import React, { useEffect, useMemo, useState } from "react";
import { Alert, Badge, Button, Card, Col, Container, Form, Row, Spinner, Table } from "react-bootstrap";
import { RefreshCw, Search } from "lucide-react";
import { CallAPIGetPromise } from "Common/commaon";
import "./billing-alerts.css";
type PaymentAlert = {
  amount?: string | number;
  company_name?: string;
  created_at?: string;
  failure_reason?: string;
  gateway: string;
  priority?: string;
  status: string;
  transaction_id: string;
  user_email?: string;
  user_first_name?: string;
  user_last_name?: string;
};

function formatDate(value?: string | null) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
}

function titleCase(value?: string) {
  return String(value || "")
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function statusBadge(status = "") {
  if (status === "failed") return "danger";
  if (status === "succeeded") return "success";
  return "secondary";
}

export default function PaymentAlerts() {
  const [alerts, setAlerts] = useState<PaymentAlert[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [gatewayFilter, setGatewayFilter] = useState("all");
  const [search, setSearch] = useState("");

  const loadAlerts = async () => {
    setLoading(true);
    setError("");
    try {
      const response: any = await CallAPIGetPromise("/v2/api/billing-alerts/payment-alerts?limit=500");
      setAlerts(response?.data || []);
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || "Failed to load payment alerts.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAlerts();
  }, []);

  const filteredAlerts = useMemo(() => {
    const query = search.trim().toLowerCase();
    return alerts.filter((alert) => {
      if (statusFilter !== "all" && alert.status !== statusFilter) return false;
      if (gatewayFilter !== "all" && alert.gateway !== gatewayFilter) return false;
      if (!query) return true;
      const customer = [alert.user_first_name, alert.user_last_name].filter(Boolean).join(" ");
      return [
        customer,
        alert.user_email,
        alert.company_name,
        alert.gateway,
        alert.status,
        alert.transaction_id,
        alert.failure_reason,
      ]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(query));
    });
  }, [alerts, gatewayFilter, search, statusFilter]);

  return (
    <div className="page-content billing-cards-page billing-payment-alerts-page">
      <Container fluid>
        <div className="billing-cards-page__toolbar">
            <Button variant="light" onClick={loadAlerts} disabled={loading}>
              <RefreshCw size={16} className="me-1" /> Refresh
            </Button>
        </div>

      {error ? <Alert variant="danger">{error}</Alert> : null}

      <Card className="billing-filters-card mb-3">
        <Card.Body>
          <Row className="g-3">
            <Col md={4}>
              <div className="app-search-field">
                <Search className="app-field-icon" />
                <Form.Control
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Search customer, email, company, transaction..."
                  aria-label="Search payment alerts"
                />
              </div>
            </Col>
            <Col md={3}>
              <Form.Select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
                <option value="all">All Statuses</option>
                <option value="failed">Failed</option>
                <option value="succeeded">Succeeded</option>
              </Form.Select>
            </Col>
            <Col md={3}>
              <Form.Select value={gatewayFilter} onChange={(event) => setGatewayFilter(event.target.value)}>
                <option value="all">All Gateways</option>
                <option value="stripe">Stripe</option>
                <option value="deluxe">Deluxe</option>
              </Form.Select>
            </Col>
            <Col md={2} className="d-flex align-items-center">
              <span className="billing-result-count"><strong>{filteredAlerts.length}</strong> shown</span>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Card className="billing-alerts-card">
        <Card.Body>
          {loading ? (
            <div className="py-5 text-center"><Spinner animation="border" /></div>
          ) : filteredAlerts.length === 0 ? (
            <Alert variant="light" className="mb-0">No payment alerts found.</Alert>
          ) : (
            <div className="table-responsive">
              <Table hover className="billing-alerts-table billing-payment-alerts-table align-middle mb-0">
                <thead>
                  <tr>
                    <th>Status</th>
                    <th>Customer</th>
                    <th>Company</th>
                    <th>Gateway</th>
                    <th>Amount</th>
                    <th>Transaction</th>
                    <th>Failure Reason</th>
                    <th>Created</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredAlerts.map((alert) => {
                    const customer = [alert.user_first_name, alert.user_last_name].filter(Boolean).join(" ") || "—";
                    return (
                      <tr key={`${alert.gateway}-${alert.transaction_id}-${alert.status}`}>
                        <td>
                          <Badge bg={statusBadge(alert.status)}>{titleCase(alert.status)}</Badge>
                          {alert.priority === "high" ? <div className="small text-danger mt-1">High priority</div> : null}
                        </td>
                        <td>
                          <div className="fw-semibold">{customer}</div>
                          <div className="small text-muted">{alert.user_email || "—"}</div>
                        </td>
                        <td>{alert.company_name || "—"}</td>
                        <td>{titleCase(alert.gateway) || "—"}</td>
                        <td>${Number(alert.amount || 0).toFixed(2)}</td>
                        <td className="billing-transaction-id" title={alert.transaction_id}>{alert.transaction_id || "—"}</td>
                        <td>{alert.failure_reason || "—"}</td>
                        <td>{formatDate(alert.created_at)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </div>
          )}
        </Card.Body>
      </Card>
      </Container>
    </div>
  );
}
